'use client';

import React, { useMemo, useState } from 'react';
import { usePlanner } from '../plannerContext';
import { offeredSeasons, prerequisiteClauses, programColor, requirementsForCourse, shortCode } from '../plannerModel';
import { ProgramDot, StatusPill } from '../PlannerPrimitives';

type GroupBy = 'term' | 'requirement' | 'department';
type Scope = 'planned' | 'earned' | 'all';

interface CourseRow {
  code: string;
  termName: string | null;
  order: number;
  earned: boolean;
}

function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: Array<[T, string]>; onChange: (v: T) => void; label: string }) {
  return (
    <div className="inline-flex rounded-lg bg-[var(--ss-surface-subtle)] p-0.5 ring-1 ring-inset ring-[var(--ss-border)]" role="group" aria-label={label}>
      {options.map(([v, text]) => (
        <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}
          className={`rounded-md px-2.5 py-1 text-xs font-semibold ${value === v ? 'bg-[var(--ss-surface)] text-[var(--ss-text)] shadow-e1' : 'text-[var(--ss-text-muted)]'}`}>{text}</button>
      ))}
    </div>
  );
}

function CourseTile({ row }: { row: CourseRow }) {
  const { index, completed, placement, programs, issuesByCode, nameOf, creditsOf, open, selectedCode } = usePlanner();
  const counts = requirementsForCourse(row.code, programs);
  const clauses = row.earned ? [] : prerequisiteClauses(row.code, row.order, index, completed, placement);
  const unmet = clauses.filter((c) => c.status === 'missing' || c.status === 'late').length;
  const seasons = offeredSeasons(row.code);
  const relevant = new Set(programs.flatMap((p) => p.requirements.filter((r) => r.openCount > 0).flatMap((r) => r.pool)));
  const unlocks = (index.dependents.get(row.code) || []).filter((d) => relevant.has(d) || placement.has(d)).length;
  const issue = issuesByCode.has(row.code);
  return (
    <button
      type="button"
      data-plan-code={row.code}
      onClick={(e) => open({ kind: 'course', code: row.code, anchor: e.currentTarget })}
      aria-pressed={selectedCode === row.code}
      className={`flex h-full flex-col gap-2.5 rounded-2xl border bg-[var(--ss-surface)] p-3.5 text-left transition-[border-color,box-shadow] duration-150 hover:border-[var(--ss-text-muted)] motion-reduce:transition-none ${selectedCode === row.code ? 'border-[var(--ss-brand)] ring-2 ring-[color-mix(in_srgb,var(--ss-brand)_25%,transparent)]' : 'border-[var(--ss-border)]'}`}
      style={{ boxShadow: counts.length ? `inset 3px 0 0 ${programColor(counts[0].programIndex)}` : undefined }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-snug">{nameOf(row.code)}</p>
          <p className="mt-0.5 font-mono text-[11px] text-[var(--ss-text-muted)]">{shortCode(row.code)} · {creditsOf(row.code) || '?'} cr</p>
        </div>
        {row.earned ? <StatusPill tone="earned">Earned</StatusPill> : <StatusPill tone="planned">{row.termName}</StatusPill>}
      </div>
      <div className="flex flex-col gap-1">
        {counts.slice(0, 2).map((req) => (
          <span key={req.key} className="flex items-center gap-1.5 text-xs"><ProgramDot index={req.programIndex} /><span className="truncate">{req.title}</span></span>
        ))}
        {counts.length > 2 ? <span className="pl-3.5 text-[11px] text-[var(--ss-text-muted)]">+{counts.length - 2} more</span> : null}
        {!counts.length ? <span className="text-xs text-[var(--ss-text-muted)]">Not counted toward these programs</span> : null}
      </div>
      <div className="mt-auto flex flex-wrap gap-1.5 text-[11px]">
        {issue ? <StatusPill tone="missing">Plan issue</StatusPill> : null}
        {!row.earned && clauses.length ? (unmet ? <StatusPill tone="missing">{unmet} prereq{unmet === 1 ? '' : 's'} missing</StatusPill> : <StatusPill tone="earned">Prereqs covered</StatusPill>) : null}
        {unlocks ? <StatusPill tone="info">Unlocks {unlocks}</StatusPill> : null}
        {seasons ? <StatusPill tone="neutral">{seasons.filter((s) => s !== 'Winter').join('/')}</StatusPill> : null}
      </div>
    </button>
  );
}

export default function CoursesView() {
  const { terms, completed, programs, index, creditsOf } = usePlanner();
  const [groupBy, setGroupBy] = useState<GroupBy>('term');
  const [scope, setScope] = useState<Scope>('planned');

  const rows = useMemo<CourseRow[]>(() => {
    const planned: CourseRow[] = [];
    terms.forEach((term, order) => term.items.forEach(({ item }) => {
      const code = item.kind === 'course_list' ? item.selectedCourseCode : item.code;
      if (code) planned.push({ code: code.replace(/[^0-9]/g, ''), termName: term.name, order, earned: false });
    }));
    const earned = Array.from(completed).map((code) => ({ code, termName: null, order: -1, earned: true }));
    return scope === 'planned' ? planned : scope === 'earned' ? earned : [...earned, ...planned];
  }, [terms, completed, scope]);

  const groups = useMemo(() => {
    const map = new Map<string, { title: string; accent?: string; rows: CourseRow[] }>();
    const push = (key: string, title: string, row: CourseRow, accent?: string) => {
      const group = map.get(key) || { title, accent, rows: [] };
      group.rows.push(row);
      map.set(key, group);
    };
    rows.forEach((row) => {
      if (groupBy === 'term') push(row.earned ? '0-earned' : `1-${String(row.order).padStart(3, '0')}`, row.termName || 'Earned', row);
      else if (groupBy === 'department') {
        const subject = row.code.slice(2, 5);
        const name = index.byCode.get(row.code)?.department;
        push(subject, name ? `${name} (${subject})` : `Subject ${subject}`, row);
      }
      else {
        const reqs = requirementsForCourse(row.code, programs);
        if (!reqs.length) push('zz', 'Not counted toward these programs', row);
        reqs.slice(0, 1).forEach((req) => push(req.key, `${req.title} · ${req.degreeName}`, row, programColor(req.programIndex)));
      }
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([, group]) => group);
  }, [rows, groupBy, programs, index]);

  const totalCredits = rows.reduce((sum, row) => sum + (creditsOf(row.code) || 0), 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[var(--ss-text-muted)]"><b className="text-[var(--ss-text)]">{rows.length}</b> courses · <b className="text-[var(--ss-text)]">{totalCredits}</b> credits</p>
        <div className="flex flex-wrap gap-2">
          <Segmented label="Which courses" value={scope} onChange={setScope} options={[['planned', 'Planned'], ['earned', 'Earned'], ['all', 'All']]} />
          <Segmented label="Group by" value={groupBy} onChange={setGroupBy} options={[['term', 'Term'], ['requirement', 'Requirement'], ['department', 'Subject']]} />
        </div>
      </div>
      {!rows.length ? <p className="rounded-2xl border border-dashed border-[var(--ss-border)] p-8 text-center text-sm text-[var(--ss-text-muted)]">{scope === 'earned' ? 'No completed courses on record yet.' : 'No courses planned yet. Fill a requirement slot or add a course to a term.'}</p> : null}
      {groups.map((group) => (
        <section key={group.title} className="flex flex-col gap-2.5">
          <h3 className="flex items-center gap-2 text-[13px] font-bold">
            {group.accent ? <span className="h-3.5 w-1 rounded-full" style={{ backgroundColor: group.accent }} aria-hidden="true" /> : null}
            {group.title}
            <span className="font-normal text-[var(--ss-text-muted)]">· {group.rows.length} course{group.rows.length === 1 ? '' : 's'}, {group.rows.reduce((s, r) => s + (creditsOf(r.code) || 0), 0)} cr</span>
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {group.rows.map((row) => <CourseTile key={`${row.code}-${row.order}`} row={row} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
